/**
 * Апскейл NijiLucid на WebGPU (app/lib/nijilucid, див. VENDORED.md) — другий
 * рушій поруч із Anime4K.js на WebGL, який плеєр уже мав.
 *
 * Те саме, що в плеєрі shiki.sgezha.art (apps/player/useUpscaler.ts): рівень
 * продуктивності (які за розміром моделі ганяти), режим (рекомендовані
 * пресети CuNNy / ARNet та режими сумісності Anime4K), роздільність,
 * монітор продуктивності поверх відео й тест GPU, що підбирає рівень під
 * залізо.
 *
 * Сам рушій — кілька мегабайтів шейдерів, і вантажиться він лише тоді, коли
 * апскейл справді вмикають. Моделі всередині теж ліниві — по чанку на ефект.
 */

let engine = null

const STORAGE_KEY = 'nijilucid_settings'

export const NIJI_DEFAULTS = {
  enabled: false,
  tier: 'performance',
  modeId: 'builtin-mode-a',
  resolution: '4k',
  monitor: 'off',
  hudCollapsed: false,
  hudPosition: 'top-left',
  hudWidth: null,
  benchmark: null
}

export const NIJI_TIER_OPTIONS = [
  { value: 'performance', label: 'Швидко' },
  { value: 'balanced', label: 'Збалансовано' },
  { value: 'quality', label: 'Якість' },
  { value: 'ultra', label: 'Ультра' }
]

export const NIJI_MODE_GROUPS = [
  {
    label: 'Рекомендовані пресети',
    options: [
      { value: 'recommended-detail-preserving', label: 'Збереження деталей' },
      { value: 'recommended-compression-cleanup', label: 'Очищення після стиснення' },
      { value: 'recommended-soft-style', label: "М'який стиль" }
    ]
  },
  {
    label: 'Режими сумісності Anime4K',
    options: [
      { value: 'builtin-mode-a', label: 'Mode A' },
      { value: 'builtin-mode-b', label: 'Mode B' },
      { value: 'builtin-mode-c', label: 'Mode C' },
      { value: 'builtin-mode-aa', label: 'Mode A+A' },
      { value: 'builtin-mode-bb', label: 'Mode B+B' },
      { value: 'builtin-mode-ca', label: 'Mode C+A' }
    ]
  }
]

export const NIJI_RESOLUTION_OPTIONS = [
  { value: 'native', label: 'Вихідна' },
  { value: '720p', label: '720p' },
  { value: '1080p', label: '1080p' },
  { value: '2k', label: '2K (1440p)' },
  { value: '4k', label: '4K (2160p)' },
  { value: 'x2', label: 'x2' },
  { value: 'x4', label: 'x4' },
  { value: 'x8', label: 'x8' }
]

export const NIJI_MONITOR_OPTIONS = [
  { value: 'off', label: 'Вимк.' },
  { value: 'lite', label: 'Полегшений' },
  { value: 'gpu', label: 'Діагностика GPU' }
]

const MODE_IDS = new Set(NIJI_MODE_GROUPS.flatMap((g) => g.options.map((o) => o.value)))
const TIERS = new Set(NIJI_TIER_OPTIONS.map((o) => o.value))
const RESOLUTIONS = new Set(NIJI_RESOLUTION_OPTIONS.map((o) => o.value))
const MONITORS = new Set(NIJI_MONITOR_OPTIONS.map((o) => o.value))
const HUD_POSITIONS = new Set(['top-left', 'top-right', 'bottom-left', 'bottom-right'])

export const nijiModeLabel = (modeId) => {
  for (const group of NIJI_MODE_GROUPS) {
    const found = group.options.find((o) => o.value === modeId)
    if (found) return found.label
  }
  return modeId
}

export const nijiTierLabel = (tier) => NIJI_TIER_OPTIONS.find((o) => o.value === tier)?.label || tier

/** Чи вміє браузер WebGPU і requestVideoFrameCallback — без них рушій не піде */
export const checkNijiSupport = () => {
  if (import.meta.server) return false
  try {
    return (
      Boolean(navigator.gpu) &&
      typeof HTMLVideoElement.prototype.requestVideoFrameCallback === 'function'
    )
  } catch (e) {
    return false
  }
}

export const ensureNijiEngine = async () => {
  if (engine) return engine
  if (import.meta.server) return null
  try {
    engine = await import('~/lib/nijilucid')
    return engine
  } catch (err) {
    console.error('[player] рушій NijiLucid не завантажився', err)
    return null
  }
}

export const getNijiEngine = () => engine

export const loadNijiSettings = () => {
  if (import.meta.server) return { ...NIJI_DEFAULTS }
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null') || {}
    const out = {}
    if (typeof raw.enabled === 'boolean') out.enabled = raw.enabled
    if (TIERS.has(raw.tier)) out.tier = raw.tier
    if (MODE_IDS.has(raw.modeId)) out.modeId = raw.modeId
    if (RESOLUTIONS.has(raw.resolution)) out.resolution = raw.resolution
    if (MONITORS.has(raw.monitor)) out.monitor = raw.monitor
    if (typeof raw.hudCollapsed === 'boolean') out.hudCollapsed = raw.hudCollapsed
    if (HUD_POSITIONS.has(raw.hudPosition)) out.hudPosition = raw.hudPosition
    if (Number.isFinite(raw.hudWidth)) out.hudWidth = raw.hudWidth
    if (raw.benchmark && TIERS.has(raw.benchmark.tier)) out.benchmark = raw.benchmark
    return { ...NIJI_DEFAULTS, ...out }
  } catch (e) {
    return { ...NIJI_DEFAULTS }
  }
}

export const saveNijiSettings = (settings) => {
  if (import.meta.server) return
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings))
  } catch (e) {}
}
